import { useEffect, useRef } from 'react'

import { getMarkerFootnoteNumberBySourceKey } from '@/agent-v2/references/agent-v2-citation-labels'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'
import { useAppDispatch } from '@/store/store-hooks'
import { DraftWorkspaceActions } from '../store/slice'

/** Keeps the store up to date with the footnote numbers the open draft shows, so the source tab can display the same numbers. */
export function useSyncDraftFootnoteNumbers(props: { markdown: string | null; referencesByCitationId: Record<string, AgentV2Reference> }) {
  const { markdown, referencesByCitationId } = props
  const dispatch = useAppDispatch()
  const storedRef = useRef<Partial<{ [sourceKey: string]: number }>>({})

  // Runs on every edit, but most edits leave the numbering alone, so only a changed lookup reaches the store.
  useEffect(() => {
    if (markdown === null) return
    const footnoteNumberBySourceKey = getMarkerFootnoteNumberBySourceKey(markdown, referencesByCitationId)
    if (isSameNumbering(storedRef.current, footnoteNumberBySourceKey)) return
    storedRef.current = footnoteNumberBySourceKey
    dispatch(DraftWorkspaceActions.setDraftFootnoteNumbers({ footnoteNumberBySourceKey }))
  }, [dispatch, markdown, referencesByCitationId])

  useEffect(() => {
    return () => {
      dispatch(DraftWorkspaceActions.clearDraftFootnoteNumbers())
    }
  }, [dispatch])
}

function isSameNumbering(a: Partial<{ [sourceKey: string]: number }>, b: Partial<{ [sourceKey: string]: number }>): boolean {
  const aKeys = Object.keys(a)
  return aKeys.length === Object.keys(b).length && aKeys.every((key) => a[key] === b[key])
}
